import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { CalendarEvent } from "../../hooks/useCalendarEvents";

export interface PendingClientsBannerProps {
  /** The yellow rows — 1-1s whose counterparty isn't a Prospo client yet. */
  events: CalendarEvent[];
  onAdd: (event: CalendarEvent) => void;
}

/** People shown before the menu starts scrolling. */
const MAX_VISIBLE_ROWS = 5;
/** Approximate height of one two-line MenuItem, in px. */
const ROW_HEIGHT = 56;
/** The sticky "Select someone…" hint above the list, in px. */
const HEADER_HEIGHT = 30;

interface PendingPerson {
  name: string;
  email: string;
  count: number;
  first: CalendarEvent;
}

/**
 * Sits above the calendar and names the people blocking a 1-1 from being
 * imported. Grouped by person rather than by meeting: four standups with the
 * same person is one client to create, not four rows to read.
 *
 * The names live behind a "View" link rather than a row of chips — the list is
 * unbounded, and a long one pushed the calendar down the page. Picking a name
 * opens the add-client dialog for that person, which is the only thing the
 * banner can actually do: the dialog creates one contact and client at a time.
 * "Create clients" opens the same menu instead of silently choosing the first
 * person, which is what it used to do.
 */
const PendingClientsBanner: React.FC<PendingClientsBannerProps> = ({ events, onAdd }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const people = useMemo<PendingPerson[]>(() => {
    const byEmail = new Map<string, PendingPerson>();
    for (const event of events) {
      const email = event.counterparty?.email;
      if (!email) continue;
      const existing = byEmail.get(email);
      if (existing) {
        existing.count += 1;
      } else {
        byEmail.set(email, {
          name: event.counterparty?.name || email,
          email,
          count: 1,
          first: event,
        });
      }
    }
    // Most-met first — the person you see weekly matters more than a one-off.
    return [...byEmail.values()].sort((a, b) => b.count - a.count);
  }, [events]);

  if (people.length === 0) return null;

  const isOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);

  const pick = (person: PendingPerson) => {
    closeMenu();
    onAdd(person.first);
  };

  return (
    <Alert
      severity="info"
      sx={{
        mb: 1.5,
        borderRadius: "10px",
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
        {people.length === 1
          ? "1 person you meet is not a Prospo client"
          : `${people.length} people you meet are not Prospo clients`}
      </AlertTitle>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ fontSize: "0.83rem" }}>
          Their meetings can&apos;t be imported until the client exists.{" "}
          <Link
            component="button"
            type="button"
            underline="always"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              // Inherit the Alert's text colour rather than pinning one, so the
              // link stays legible whatever severity the banner uses.
              color: "inherit",
              fontSize: "0.83rem",
              fontWeight: 600,
              verticalAlign: "baseline",
            }}
          >
            View
          </Link>
        </Box>

        <Button
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ textTransform: "none", fontWeight: 600, ml: "auto" }}
        >
          Create clients
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              // Show five people, then scroll. A long list would otherwise run
              // the length of the viewport.
              maxHeight: MAX_VISIBLE_ROWS * ROW_HEIGHT + HEADER_HEIGHT,
            },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 0.75,
            display: "block",
            color: "text.secondary",
            // Sticky so the hint stays put once the list scrolls past five.
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          {people.length > MAX_VISIBLE_ROWS
            ? `Select someone to create their client (${people.length})`
            : "Select someone to create their client"}
        </Typography>
        {people.map((person) => (
          <MenuItem key={person.email} onClick={() => pick(person)}>
            <ListItemText
              primary={person.name}
              secondary={
                person.count > 1
                  ? `${person.email} · ${person.count} meetings`
                  : person.email
              }
              primaryTypographyProps={{ fontSize: "0.875rem" }}
              secondaryTypographyProps={{ fontSize: "0.75rem" }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Alert>
  );
};

export default PendingClientsBanner;
